import {
  Overlay,
  OverlayConfig,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { EditorView } from 'prosemirror-view';
import { Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SFNodeType } from '@sciflow/schema';

import { EditorService } from '../../editor.service';
import { selectSelection } from '../../editor.reducer';
import { MetadataMenuComponent } from '../metadata-menu/metadata-menu.component';
import { QuickActionsMenuComponent } from './quick-actions-menu/quick-actions-menu.component';

@Component({
  selector: 'sfo-quick-actions',
  template: '',
  standalone: false,
})
export class QuickActionsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<any> | null = null;
  private currentMode: 'node' | 'mark' | null = null;

  constructor(
    private editorService: EditorService,
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private overlayPositionBuilder: OverlayPositionBuilder,
    private store: Store,
  ) {}

  ngOnInit(): void {
    combineLatest([this.editorService.currentView, this.store.select(selectSelection)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([view, selection]) => {
        if (!view) {
          this.hideMenu();
          this.cdr.detectChanges();
          return;
        }

        const mark = this.getSelectedMark(selection);
        if (mark) {
          const target = this.findMarkElement(mark, view as EditorView);
          if (target) {
            this.showOverlay(target, 'mark', view as EditorView, mark);
            this.cdr.detectChanges();
            return;
          }
        }

        const nodeSelection = view.state.selection?.['node'];
        if (
          nodeSelection?.type?.name === SFNodeType.image ||
          nodeSelection?.type?.name === SFNodeType.heading
        ) {
          const { from } = view.state.selection;
          const domNode = view.nodeDOM(from);
          if (domNode instanceof HTMLElement) {
            this.showOverlay(domNode, 'node', view as EditorView);
            this.cdr.detectChanges();
            return;
          }
        }

        this.hideMenu();
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.hideMenu();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private showOverlay(
    targetElement: HTMLElement,
    mode: 'node' | 'mark',
    view: EditorView,
    mark?: any,
  ): void {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(targetElement)
      .withPositions(
        mode === 'mark'
          ? [
              {
                originX: 'center',
                originY: 'top',
                overlayX: 'center',
                overlayY: 'bottom',
                offsetY: -8,
              },
              {
                originX: 'center',
                originY: 'bottom',
                overlayX: 'center',
                overlayY: 'top',
                offsetY: 8,
              },
            ]
          : [
              {
                originX: 'end',
                originY: 'center',
                overlayX: 'start',
                overlayY: 'center',
                offsetX: 8,
              },
            ],
      )
      .withPush(false)
      .withFlexibleDimensions(false);

    if (!this.overlayRef) {
      const overlayConfig: OverlayConfig = {
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: false,
      };
      this.overlayRef = this.overlay.create(overlayConfig);
    } else {
      this.overlayRef.updatePositionStrategy(positionStrategy);
    }

    if (this.currentMode !== mode || !this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
      const portal =
        mode === 'mark'
          ? new ComponentPortal(MetadataMenuComponent)
          : new ComponentPortal(QuickActionsMenuComponent);
      this.componentRef = this.overlayRef.attach(portal);
      this.currentMode = mode;
    }

    if (mode === 'mark' && this.componentRef?.instance) {
      this.componentRef.instance.mark = {
        ...mark,
        type: mark?.type || 'indexEntry',
      };
      this.componentRef.instance.node = null;
      this.componentRef.instance.editorView = view;
      this.componentRef.changeDetectorRef.markForCheck();
    }

    this.overlayRef.updatePosition();
  }

  private hideMenu(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
    }
    this.componentRef = null;
    this.currentMode = null;
  }

  private findMarkElement(mark: any, view: EditorView): HTMLElement | null {
    if (mark?.type === 'anchor') {
      if (mark?.id) {
        const byDataId = document.querySelector(`a[data-id="${mark.id}"]`);
        if (byDataId instanceof HTMLElement) {
          return byDataId;
        }
      }
      if (typeof mark?.from === 'number') {
        try {
          const { node } = view.domAtPos(mark.from);
          const baseElement =
            node instanceof HTMLElement
              ? node
              : node instanceof Text
              ? node.parentElement
              : null;
          const anchorElement = baseElement?.closest('a[href]:not([data-type="xref"])');
          if (anchorElement instanceof HTMLElement) {
            return anchorElement;
          }
        } catch {
          return null;
        }
      }
      return null;
    }

    if (mark?.type === 'indexEntry') {
      if (mark?.id) {
        const byId = document.getElementById(mark.id);
        if (byId instanceof HTMLElement) {
          return byId;
        }
      }

      if (mark?.id != null) {
        const byPayload = Array.from(document.querySelectorAll('span[data-index-entry]')).find(
          (el) => {
            if (!(el instanceof HTMLElement)) {
              return false;
            }
            const payload = el.getAttribute('data-index-entry');
            if (!payload) {
              return false;
            }
            try {
              const parsed = JSON.parse(payload);
              return parsed?.id === mark.id;
            } catch {
              return false;
            }
          },
        );
        if (byPayload instanceof HTMLElement) {
          return byPayload;
        }
      }

      if (typeof mark?.from === 'number') {
        try {
          const { node } = view.domAtPos(mark.from);
          const baseElement =
            node instanceof HTMLElement
              ? node
              : node instanceof Text
              ? node.parentElement
              : null;
          const anchorElement = baseElement?.closest('span[data-index-entry]');
          if (anchorElement instanceof HTMLElement) {
            return anchorElement;
          }
        } catch {
          return null;
        }
      }
    }

    return null;
  }

  private getSelectedMark(selection: any): any | null {
    const marks = selection?.selectedMarks;
    if (!marks) {
      return null;
    }
    if (Array.isArray(marks.anchor) && marks.anchor.length > 0) {
      const mark = marks.anchor[0];
      return { ...mark, type: mark?.type || 'anchor' };
    }
    if (Array.isArray(marks.indexEntry) && marks.indexEntry.length > 0) {
      const mark = marks.indexEntry[0];
      return { ...mark, type: mark?.type || 'indexEntry' };
    }
    return null;
  }
}
